// Funnel metric cards (MIS-813 / Task 32).
//
// Renders the tenant funnel as a row of shared `Card` tiles, sourced from the
// REAL `GET /api/analytics/funnel` response. That endpoint
// (apps/worker/src/routes/analytics.ts + db/repos/analytics.ts) returns exactly:
//   { visits, leads, calls, conversions, revenueCents }
// so these cards cover the visits → matched(leads) → calls → revenue waterfall
// plus the converted-call count, with step conversion rates derived client-side.
//
// NOTE: the issue's aspirational spec lists thx/tier/SMS/bail splits on the
// funnel, but the shipped funnel repo does NOT return them (tier counts live on
// the by-utm rows instead, shown in the Attribution tab). We render only fields
// the endpoint actually provides — no fabricated metrics, all aggregates (no PII).

import type { InferResponseType } from 'hono/client';
import { Card, CardContent } from '@/components/ui/Card';
import { formatCount, formatCurrency, formatPercent } from '@/lib/analytics';
import type { ApiClient } from '@/lib/analytics-types';

// `InferResponseType` yields the UNION of every status response (200 success +
// the 400 `{ error, issues }` validation body); narrow to the success member by
// the discriminating `funnel` key before reading the funnel object.
type FunnelResponse = InferResponseType<ApiClient['api']['analytics']['funnel']['$get']>;
type FunnelSuccess = Extract<FunnelResponse, { funnel: unknown }>;
export type Funnel = FunnelSuccess['funnel'];

interface MetricCardProps {
  label: string;
  value: string;
  /** Optional sub-line, e.g. a conversion rate vs the previous step. */
  hint?: string;
}

function MetricCard({ label, value, hint }: MetricCardProps): React.ReactElement {
  return (
    <Card>
      <CardContent className="py-5">
        <div className="text-sm font-medium text-gray-500">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{value}</div>
        {hint ? <div className="mt-1 text-xs text-gray-400">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

export function FunnelCards({ funnel }: { funnel: Funnel }): React.ReactElement {
  const { visits, leads, calls, conversions, revenueCents } = funnel;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <MetricCard label="Visits" value={formatCount(visits)} />
      <MetricCard
        label="Matched leads"
        value={formatCount(leads)}
        hint={`${formatPercent(leads, visits)} of visits`}
      />
      <MetricCard
        label="Calls"
        value={formatCount(calls)}
        hint={`${formatPercent(calls, leads)} of leads`}
      />
      <MetricCard
        label="Conversions"
        value={formatCount(conversions)}
        hint={`${formatPercent(conversions, calls)} of calls`}
      />
      <MetricCard label="Revenue" value={formatCurrency(revenueCents)} />
    </div>
  );
}
